'use client';

import React, { useState } from 'react';
import { AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';

interface AlertItem {
  flockNumber: string;
  flockName: string;
  house: string;
  farm: string;
  alertType: 'daily' | 'weekly' | 'cumulative';
  currentPct: number;
  thresholdPct: number;
  cause: string;
  severity: 'critical' | 'warning';
}

const ALERTS: AlertItem[] = [
  {
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    house: 'House B1',
    farm: 'Idudi Poultry Farm',
    alertType: 'daily',
    currentPct: 0.524,
    thresholdPct: 0.5,
    cause: 'Suspected Gumboro (IBD)',
    severity: 'critical',
  },
  {
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    house: 'House A2',
    farm: 'Idudi Poultry Farm',
    alertType: 'weekly',
    currentPct: 1.82,
    thresholdPct: 2.0,
    cause: 'Infectious Bronchitis',
    severity: 'warning',
  },
];

const ALERT_TYPE_LABELS: Record<string, string> = {
  daily: 'Daily Threshold',
  weekly: '7-Day Threshold',
  cumulative: 'Cumulative Threshold',
};

export default function MortalityThresholdAlerts() {
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState<string[]>([]);

  const visible = ALERTS.filter(a => !dismissed.includes(a.flockNumber + a.alertType));

  if (visible.length === 0) return null;

  return (
    <div className="mb-6 rounded-xl border border-red-200 bg-red-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-red-100 border-b border-red-200">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600" />
          <span className="text-sm font-semibold text-red-800">
            Mortality Threshold Alerts — {visible.length} Active
          </span>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-red-500 hover:text-red-700 transition-colors"
          aria-label="Toggle alerts"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Alert items */}
      {expanded && (
        <div className="divide-y divide-red-100">
          {visible.map(alert => (
            <div key={alert.flockNumber + alert.alertType} className="flex items-start gap-3 px-4 py-3">
              <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${alert.severity === 'critical' ? 'bg-red-600' : 'bg-amber-500'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-red-900">{alert.flockNumber}</span>
                  <span className="text-xs text-red-600">{alert.flockName}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    alert.severity === 'critical' ? 'bg-red-200 text-red-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {alert.severity === 'critical' ? 'CRITICAL' : 'WARNING'}
                  </span>
                </div>
                <p className="text-xs text-red-700 mt-0.5">
                  <strong>{ALERT_TYPE_LABELS[alert.alertType]}</strong> breached — {alert.currentPct.toFixed(3)}% vs limit {alert.thresholdPct}% · {alert.house}, {alert.farm}
                </p>
                <p className="text-xs text-red-600 mt-0.5">Suspected cause: {alert.cause}</p>
              </div>
              <button
                onClick={() => setDismissed(d => [...d, alert.flockNumber + alert.alertType])}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                aria-label="Dismiss alert"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
